import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Ruler, ShieldCheck, Palette, Zap, BookOpen } from 'lucide-react';
import { NEXT_STEPS } from '../../data/productNextSteps';
import { SHADE_PAIRS, DRAPERY_PAIRS } from '../../data/productNav';

const SUPPORT = [
  { to: '/fabrics', label: 'Browse fabrics', note: 'See every color and opacity, and order free samples.', Icon: Palette },
  { to: '/measuring-installation', label: 'Measuring & installation', note: 'Professional measuring and installation, or a guide to measure yourself.', Icon: Ruler },
  { to: '/warranty-service', label: 'Warranty & service', note: 'What is covered and how we look after you after install.', Icon: ShieldCheck },
];

const GUIDE = { to: '/blackout-vs-light-filtering-vs-solar-shades', label: 'Blackout, light filtering or solar?', note: 'Our guide to choosing the right fabric for each room.', Icon: BookOpen };
const GUIDE_SLUGS = ['roller-shades', 'solar-shades', 'roman-shades', 'cellular-shades', 'zebra-shades', 'motorized-shades', 'motorized-skylights'];

const LinkRow = ({ to, label, note, Icon, testId }) => (
  <Link to={to} className="group flex items-start gap-3 py-4 border-b border-neutral-200 last:border-b-0" data-testid={testId}>
    {Icon && <Icon className="w-5 h-5 mt-0.5 text-amber-700 flex-shrink-0" />}
    <span className="flex-1">
      <span className="flex items-center gap-2 font-medium text-neutral-900 group-hover:text-amber-700 transition-colors">{label}<ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform]" /></span>
      <span className="block text-sm text-neutral-600 mt-0.5">{note}</span>
    </span>
  </Link>
);

// Natural next steps for a product page; renders nothing for pages without a mapping.
export const ProductNextSteps = ({ slug, name, supportOnly }) => {
  const s = NEXT_STEPS[slug];
  if (!s) return null;
  const counterpart = [...SHADE_PAIRS, ...DRAPERY_PAIRS].find((p) => p.std === `/products/${slug}` && p.moto);
  return (
    <section className="section-padding bg-neutral-50" data-testid="product-next-steps">
      <div className="container-xl">
        <span className="label-overline text-amber-700">Plan your project</span>
        <h2 className="mt-4 mb-10">Everything that goes with your {name}</h2>
        <div className="grid lg:grid-cols-2 gap-x-16 gap-y-10">
          {!supportOnly && (
            <div>
              <h3 className="text-lg font-medium mb-2">Often considered alongside</h3>
              {s.related.map((r) => <LinkRow key={r.to} {...r} testId={`next-related-${r.to.split('/').pop()}`} />)}
            </div>
          )}
          <div>
            <h3 className="text-lg font-medium mb-2">Before you order</h3>
            <LinkRow {...s.motor} Icon={Zap} testId="next-motor-link" />
            {counterpart && <LinkRow to={counterpart.moto} label={`Motorized ${counterpart.name}`} note="The same product with quiet motorized control." Icon={Zap} testId="next-motorized-version" />}
            {GUIDE_SLUGS.includes(slug) && <LinkRow {...GUIDE} testId="next-fabric-guide" />}
            {SUPPORT.map((r) => <LinkRow key={r.to} {...r} testId={`next-${r.to.slice(1)}`} />)}
          </div>
        </div>
        {!supportOnly && <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link to="/consultation" className="btn-primary inline-flex items-center gap-2" data-testid="next-consultation-link">Book a free consultation <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/quote" className="btn-outline inline-flex items-center gap-2" data-testid="next-quote-link">Request a quote</Link>
        </div>}
      </div>
    </section>
  );
};
